import { GetStaticPaths, GetStaticProps } from "next";

import Category from "../components/Category";
import Layout from "../components/Layout";
import Page from "../components/Page";
import Post from "../components/Post";
import Treatment from "../components/Treatment";
import {
  CategoryFragment,
  PageFragment,
  PostFragment,
  Stage,
  TreatmentFragment,
} from "../interfaces";
import getClient from "../lib/getClient";
import getCommonProps, { CommonProps } from "../lib/getCommonProps";

type Props = {
  data: PageFragment | PostFragment | TreatmentFragment | CategoryFragment;
  preview?: boolean;
} & CommonProps;

const CatchAll = ({ data, preview, ...commonProps }: Props) => {
  const renderSwitch = (data: Props["data"]) => {
    switch (data.__typename) {
      case "Page":
        return <Page page={data} />;
      case "Post":
        return <Post {...data} />;
      case "Treatment":
        return <Treatment {...data} />;
      case "Category":
        return (
          <Category
            category={data}
            posts={data.posts}
            treatments={data.treatments}
          />
        );
      default:
        return;
    }
  };

  return (
    <Layout preview={preview} {...commonProps}>
      {renderSwitch(data)}
    </Layout>
  );
};

export const getStaticProps: GetStaticProps = async (context) => {
  const commonProps = await getCommonProps(context);
  const sdk = await getClient(context.preview);
  const ptcSlugs = await sdk.PostTreatmentCategoryIdSlugs();
  const { pages: pageSlugs } = await sdk.PageSlugs();
  const slugs = [
    ...ptcSlugs.posts,
    ...ptcSlugs.treatments,
    ...ptcSlugs.categories,
    ...pageSlugs,
  ];
  const id = slugs.find(
    (ptc) => ptc.seo.slug === context.params?.slug?.slice(-1)[0]
  )?.id;

  if (!id) {
    return {
      notFound: true,
    };
  }

  const { post, treatment, category } = await sdk.PostTreatmentCategoryById({
    id: id,
    stage: context.preview ? Stage.Draft : Stage.Published,
  });
  const { page } = await sdk.PageById({
    id: id,
    stage: context.preview ? Stage.Draft : Stage.Published,
  });
  const data = page || post || treatment || category;

  return {
    props: {
      data,
      preview: context.preview || null,
      ...commonProps,
    },
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  const sdk = await getClient();
  const { posts, treatments } = await sdk.PostTreatmentCategoryIdSlugs();
  const data = [...posts, ...treatments];

  const postTreatmentPaths = data.map((item) => ({
    params: {
      slug: [
        ...item.categories.map((category) => category.seo?.slug),
        item.seo?.slug,
      ],
    },
  }));

  // Remove last element to get the category paths
  const pathWithRoot = [
    ...postTreatmentPaths,
    ...postTreatmentPaths.map((path) => ({
      params: { slug: path.params.slug.slice(0, -1) },
    })),
  ];

  // Remove root element
  const paths = pathWithRoot.filter((paths) => paths.params.slug.length > 0);

  const { pages } = await sdk.PageSlugs();

  return {
    paths: [
      ...paths,
      ...pages.map((page) => ({ params: { slug: [page.seo.slug] } })),
    ],
    fallback: false,
  };
};

export default CatchAll;
